export const API_CONFIG = {
  baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003',
  endpoints: {
    // Auth
    auth: {
      session: '/v1/auth/session',
      me: '/v1/auth/me',
      twoFactor: {
        status: '/v1/auth/2fa/status',
        confirmEnrollment: '/v1/auth/2fa/confirm-enrollment',
        backupCodes: '/v1/auth/2fa/backup-codes',
        disable: '/v1/auth/2fa/disable',
        requestRecovery: '/v1/auth/2fa/request-recovery',
        completeRecovery: '/v1/auth/2fa/complete-recovery',
      },
    },
    // Sessions (all endpoints require propertyId since sessions live under properties/)
    sessions: {
      active: (propertyId: string) => `/v1/admin/sessions/active?propertyId=${propertyId}`,
      detail: (sessionId: string, propertyId: string) => `/v1/admin/sessions/${sessionId}?propertyId=${propertyId}`,
      takeover: (sessionId: string) => `/v1/admin/sessions/${sessionId}/takeover`,
      release: (sessionId: string) => `/v1/admin/sessions/${sessionId}/release`,
      message: (sessionId: string) => `/v1/admin/sessions/${sessionId}/message`,
      conversation: (sessionId: string, propertyId?: string) => `/v1/admin/sessions/${sessionId}/conversation${propertyId ? `?propertyId=${propertyId}` : ''}`,
      handoverBrief: (sessionId: string, propertyId: string) => `/v1/admin/sessions/${sessionId}/handover-brief?propertyId=${propertyId}`,
      guestContext: (propertyId: string, sessionId: string) => `/v1/sessions/${propertyId}/${sessionId}/guest-context`,
    },
    // Knowledge Base
    knowledgeBase: {
      list: (propertyId: string) => `/v1/admin/knowledge-base/${propertyId}`,
      detail: (propertyId: string, entityId: string) => `/v1/admin/knowledge-base/${propertyId}/${entityId}`,
      create: (propertyId: string) => `/v1/admin/knowledge-base/${propertyId}`,
      update: (propertyId: string, entityId: string) => `/v1/admin/knowledge-base/${propertyId}/${entityId}`,
      delete: (propertyId: string, entityId: string) => `/v1/admin/knowledge-base/${propertyId}/${entityId}`,
      refetch: (propertyId: string) => `/v1/admin/knowledge-base/${propertyId}/refetch`,
      refresh: (propertyId: string, entityId: string) => `/v1/admin/knowledge-base/${propertyId}/${entityId}/refresh`,
      scrapingStatus: (propertyId: string) => `/v1/admin/knowledge-base/${propertyId}/scraping-status`,
      stats: (propertyId: string) => `/v1/admin/knowledge-base/${propertyId}/stats`,
    },
    // Handovers
    handovers: {
      assign: (id: string) => `/v1/admin/handovers/${id}/assign`,
      resolve: (id: string) => `/v1/admin/handovers/${id}/resolve`,
    },
    // Analytics
    analytics: {
      summary: (propertyId: string) => `/v1/admin/analytics/${propertyId}/summary`,
      dashboard: (propertyId: string) => `/v1/admin/analytics/${propertyId}/dashboard`,
      links: (propertyId: string) => `/v1/admin/analytics/${propertyId}/links`,
      trends: (propertyId: string) => `/v1/admin/analytics/${propertyId}/trends`,
      funnel: (propertyId: string) => `/v1/admin/analytics/${propertyId}/funnel`,
      topics: (propertyId: string) => `/v1/admin/analytics/${propertyId}/topics`,
      kbEffectiveness: (propertyId: string) => `/v1/admin/analytics/${propertyId}/kb-effectiveness`,
      bookingsByCategory: (propertyId: string) => `/v1/admin/analytics/${propertyId}/bookings-by-category`,
      export: (propertyId: string) => `/v1/admin/analytics/${propertyId}/export`,
    },
    // Properties
    properties: {
      list: '/v1/admin/properties',
      create: '/v1/admin/properties',
      update: (propertyId: string) => `/v1/admin/properties/${propertyId}`,
      transferOwnership: (propertyId: string) => `/v1/admin/properties/${propertyId}/transfer-ownership`,
      qrCodes: (propertyId: string, params?: { type?: string; size?: number }) => {
        const search = new URLSearchParams();
        if (params?.type) search.set('type', params.type);
        if (params?.size) search.set('size', String(params.size));
        const query = search.toString();
        return `/v1/admin/properties/${propertyId}/qr-codes${query ? `?${query}` : ''}`;
      },
    },
    // Commercial provisioning
    provisioning: {
      salesAssisted: '/v1/admin/provisioning/sales-assisted',
    },
    adminUsers: {
      list: '/v1/admin/users',
      detail: (userId: string) => `/v1/admin/users/${userId}`,
    },
    // Audit history
    audit: {
      // Search is client-side over loaded entries (Firestore has no full-text search)
      list: (params?: { scope?: 'platform' | 'organization' | 'property'; orgId?: string; propertyId?: string; limit?: number }) => {
        const search = new URLSearchParams();
        if (params?.scope) search.set('scope', params.scope);
        if (params?.orgId) search.set('orgId', params.orgId);
        if (params?.propertyId) search.set('propertyId', params.propertyId);
        if (typeof params?.limit === 'number') search.set('limit', String(params.limit));
        const query = search.toString();
        return `/v1/admin/audit${query ? `?${query}` : ''}`;
      },
    },
    // Guest Profiles
    guestProfiles: {
      list: (propertyId: string) => `/v1/admin/guest-profiles/${propertyId}`,
      detail: (propertyId: string, profileId: string) => `/v1/admin/guest-profiles/${propertyId}/${profileId}`,
      update: (propertyId: string, profileId: string) => `/v1/admin/guest-profiles/${propertyId}/${profileId}`,
      merge: (propertyId: string) => `/v1/admin/guest-profiles/${propertyId}/merge`,
      tabCounts: (propertyId: string) => `/v1/admin/guest-profiles/${propertyId}/tab-counts`,
      segments: (propertyId: string) => `/v1/admin/guest-profiles/${propertyId}/segments`,
      segmentExplanations: (propertyId: string, profileId: string) => `/v1/admin/guest-profiles/${propertyId}/${profileId}/segments`,
      stays: (propertyId: string, profileId: string) => `/v1/admin/guest-profiles/${propertyId}/${profileId}/stays`,
      communication: (propertyId: string, profileId: string) => `/v1/admin/guest-profiles/${propertyId}/${profileId}/communication`,
      channelPreference: (propertyId: string, profileId: string) => `/v1/admin/guest-profiles/${propertyId}/${profileId}/channel-preference`,
      message: (propertyId: string, profileId: string) => `/v1/admin/guest-profiles/${propertyId}/${profileId}/message`,
      search: (propertyId: string) => `/v1/admin/guest-profiles/${propertyId}/search`,
      export: (propertyId: string) => `/v1/admin/guest-profiles/${propertyId}/export`,
      create: (propertyId: string) => `/v1/admin/guest-profiles/${propertyId}`,
      checkIn: (propertyId: string, profileId: string) => `/v1/admin/guest-profiles/${propertyId}/${profileId}/check-in`,
      checkOut: (propertyId: string, profileId: string) => `/v1/admin/guest-profiles/${propertyId}/${profileId}/check-out`,
      revenue: (propertyId: string, profileId: string) => `/v1/admin/guest-profiles/${propertyId}/${profileId}/revenue`,
      mergeSuggestions: (propertyId: string) => `/v1/admin/guest-profiles/${propertyId}/merge-suggestions`,
      confirmMerge: (propertyId: string, suggestionId: string) => `/v1/admin/guest-profiles/${propertyId}/merge-suggestions/${suggestionId}/confirm`,
      dismissMerge: (propertyId: string, suggestionId: string) => `/v1/admin/guest-profiles/${propertyId}/merge-suggestions/${suggestionId}/dismiss`,
    },
    // Automation
    automation: {
      triggers: {
        list: (propertyId: string) => `/v1/admin/automation/${propertyId}/triggers`,
        detail: (propertyId: string, triggerId: string) => `/v1/admin/automation/${propertyId}/triggers/${triggerId}`,
        create: (propertyId: string) => `/v1/admin/automation/${propertyId}/triggers`,
        update: (propertyId: string, triggerId: string) => `/v1/admin/automation/${propertyId}/triggers/${triggerId}`,
        delete: (propertyId: string, triggerId: string) => `/v1/admin/automation/${propertyId}/triggers/${triggerId}`,
        dryRun: (propertyId: string, triggerId: string) => `/v1/admin/automation/${propertyId}/triggers/${triggerId}/dry-run`,
        reach: (propertyId: string, triggerId: string) => `/v1/admin/automation/${propertyId}/triggers/${triggerId}/reach`,
        seedDefaultPack: (propertyId: string) => `/v1/admin/automation/${propertyId}/seed-default-pack`,
        defaultPackInfo: (propertyId: string) => `/v1/admin/automation/${propertyId}/default-pack-info`,
      },
      recommendations: {
        list: (propertyId: string) => `/v1/admin/automation/${propertyId}/recommendations`,
        accept: (propertyId: string, recommendationId: string) => `/v1/admin/automation/${propertyId}/recommendations/${recommendationId}/accept`,
      },
      templates: {
        list: (propertyId: string) => `/v1/admin/automation/${propertyId}/templates`,
        detail: (propertyId: string, templateId: string) => `/v1/admin/automation/${propertyId}/templates/${templateId}`,
        create: (propertyId: string) => `/v1/admin/automation/${propertyId}/templates`,
        update: (propertyId: string, templateId: string) => `/v1/admin/automation/${propertyId}/templates/${templateId}`,
        delete: (propertyId: string, templateId: string) => `/v1/admin/automation/${propertyId}/templates/${templateId}`,
        preview: (propertyId: string, templateId: string) => `/v1/admin/automation/${propertyId}/templates/${templateId}/preview`,
      },
      analytics: (propertyId: string) => `/v1/admin/automation/${propertyId}/analytics`,
    },
    // Notifications
    notifications: {
      list: (propertyId: string) => `/v1/admin/notifications/${propertyId}`,
      retry: (propertyId: string, notificationId: string) => `/v1/admin/notifications/${propertyId}/${notificationId}/retry`,
      stats: (propertyId: string) => `/v1/admin/notifications/${propertyId}/stats`,
    },
    // Campaigns
    campaigns: {
      list: (propertyId: string) => `/v1/admin/campaigns/${propertyId}`,
      detail: (propertyId: string, campaignId: string) => `/v1/admin/campaigns/${propertyId}/${campaignId}`,
      create: (propertyId: string) => `/v1/admin/campaigns/${propertyId}`,
      update: (propertyId: string, campaignId: string) => `/v1/admin/campaigns/${propertyId}/${campaignId}`,
      send: (propertyId: string, campaignId: string) => `/v1/admin/campaigns/${propertyId}/${campaignId}/send`,
      pause: (propertyId: string, campaignId: string) => `/v1/admin/campaigns/${propertyId}/${campaignId}/pause`,
      resume: (propertyId: string, campaignId: string) => `/v1/admin/campaigns/${propertyId}/${campaignId}/resume`,
      metrics: (propertyId: string, campaignId: string) => `/v1/admin/campaigns/${propertyId}/${campaignId}/metrics`,
    },
    // Onboarding
    onboarding: {
      progress: (propertyId: string) => `/v1/admin/onboarding/${propertyId}`,
      skipStep: (propertyId: string) => `/v1/admin/onboarding/${propertyId}/skip-step`,
      verify: (propertyId: string) => `/v1/admin/onboarding/${propertyId}/verify-widget`,
    },
    // Self-serve bootstrap
    bootstrap: {
      create: '/v1/self-serve/bootstrap',
    },
    // Organizations
    organizations: {
      list: '/v1/admin/organizations',
      create: '/v1/admin/organizations',
      update: (orgId: string) => `/v1/admin/organizations/${orgId}`,
    },
    // Brands
    brands: {
      list: '/v1/admin/brands',
      create: '/v1/admin/brands',
      detail: (brandId: string) => `/v1/admin/brands/${brandId}`,
      update: (brandId: string) => `/v1/admin/brands/${brandId}`,
      delete: (brandId: string) => `/v1/admin/brands/${brandId}`,
      properties: (brandId: string) => `/v1/admin/brands/${brandId}/properties`,
    },
    // Public pricing (no auth)
    pricing: '/v1/pricing',
    // Billing
    billing: {
      overview: '/v1/admin/billing/overview',
      customerHealth: '/v1/admin/billing/customer-health',
      customerHealthDetail: (orgId: string) => `/v1/admin/billing/customer-health/${orgId}`,
      plan: (orgId: string) => `/v1/admin/billing/${orgId}`,
      usage: (orgId: string) => `/v1/admin/billing/${orgId}/usage`,
      plans: (orgId: string) => `/v1/admin/billing/${orgId}/plans`,
      invoices: (orgId: string) => `/v1/admin/billing/${orgId}/invoices`,
      // POST /plan to select, PUT /plan to change, DELETE /plan to cancel
      selectPlan: (orgId: string) => `/v1/admin/billing/${orgId}/plan`,
      changePlan: (orgId: string) => `/v1/admin/billing/${orgId}/plan`,
      cancelPlan: (orgId: string) => `/v1/admin/billing/${orgId}/plan`,
      portal: (orgId: string) => `/v1/admin/billing/${orgId}/portal`,
      margin: (orgId: string) => `/v1/admin/billing/${orgId}/margin`,
      pmsSetup: (orgId: string) => `/v1/admin/billing/${orgId}/pms-setup`,
      contact: (orgId: string) => `/v1/admin/billing/${orgId}/contact`,
      customPricing: (orgId: string) => `/v1/admin/billing/${orgId}/custom-pricing`,
      paymentTerms: (orgId: string) => `/v1/admin/billing/${orgId}/payment-terms`,
      recordPayment: (orgId: string) => `/v1/admin/billing/${orgId}/record-payment`,
      setupAch: (orgId: string) => `/v1/admin/billing/${orgId}/setup-ach`,
      migratePrice: (orgId: string) => `/v1/admin/billing/${orgId}/migrate-price`,
      assignPlan: (orgId: string) => `/v1/admin/billing/${orgId}/assign-plan`,
    },
    // Channels
    channels: {
      twilioStatus: (pid: string) => `/v1/admin/channels/${pid}/twilio/status`,
      twilioProvision: (pid: string) => `/v1/admin/channels/${pid}/twilio/provision`,
      twilioNumberSearch: (pid: string) => `/v1/admin/channels/${pid}/twilio/numbers/search`,
      twilioNumberBuy: (pid: string) => `/v1/admin/channels/${pid}/twilio/numbers/buy`,
      twilioBrand: (pid: string) => `/v1/admin/channels/${pid}/twilio/brand`,
      twilioCampaign: (pid: string) => `/v1/admin/channels/${pid}/twilio/campaign`,
      twilioWebhooks: (pid: string) => `/v1/admin/channels/${pid}/twilio/webhooks`,
      twilioCustomerProfile: (pid: string) => `/v1/admin/channels/${pid}/twilio/customer-profile`,
      twilioWhatsapp: (pid: string) => `/v1/admin/channels/${pid}/twilio/whatsapp`,
      twilioRefreshStatus: (pid: string) => `/v1/admin/channels/${pid}/twilio/refresh-status`,
      twilioImport: (pid: string) => `/v1/admin/channels/${pid}/twilio/import`,
      twilioTest: (pid: string) => `/v1/admin/channels/${pid}/twilio/test`,
    },
    // Feedback
    feedback: {
      list: (propertyId: string) => `/v1/admin/feedback/${propertyId}`,
      analytics: (propertyId: string) => `/v1/admin/feedback/${propertyId}/analytics`,
      detail: (propertyId: string, feedbackId: string) => `/v1/admin/feedback/${propertyId}/${feedbackId}`,
      action: (propertyId: string, feedbackId: string) => `/v1/admin/feedback/${propertyId}/${feedbackId}/action`,
      guest: (propertyId: string, profileId: string) => `/v1/admin/feedback/${propertyId}/guest/${profileId}`,
    },
    // Global Guests
    globalGuests: {
      list: (orgId: string) => `/v1/admin/global-guests/${orgId}`,
      detail: (orgId: string, globalGuestId: string) => `/v1/admin/global-guests/${orgId}/${globalGuestId}`,
      unlink: (orgId: string, globalGuestId: string) => `/v1/admin/global-guests/${orgId}/${globalGuestId}/unlink`,
      merge: (orgId: string) => `/v1/admin/global-guests/${orgId}/merge`,
      mergeSuggestions: (orgId: string) => `/v1/admin/global-guests/${orgId}/merge-suggestions`,
      confirmMerge: (orgId: string, suggestionId: string) => `/v1/admin/global-guests/${orgId}/merge-suggestions/${suggestionId}/confirm`,
      dismissMerge: (orgId: string, suggestionId: string) => `/v1/admin/global-guests/${orgId}/merge-suggestions/${suggestionId}/dismiss`,
      crossPropertyReport: (orgId: string) => `/v1/admin/global-guests/${orgId}/reports/cross-property`,
      topGuests: (orgId: string) => `/v1/admin/global-guests/${orgId}/reports/top-guests`,
    },
    // ROI Dashboard
    roi: {
      summary: (propertyId: string) => `/v1/admin/roi/${propertyId}/summary`,
      channelBreakdown: (propertyId: string) => `/v1/admin/roi/${propertyId}/channel-breakdown`,
      conversions: (propertyId: string) => `/v1/admin/roi/${propertyId}/conversions`,
    },
    // Team Management
    team: {
      members: (propertyId: string) => `/v1/admin/users?propertyId=${propertyId}`,
      roles: '/v1/admin/roles',
      invite: '/v1/admin/users/invite',
      bulkInvite: '/v1/admin/users/invite/bulk',
      membership: (membershipId: string) => `/v1/admin/memberships/${membershipId}`,
      suspend: (userId: string) => `/v1/admin/users/${userId}/suspend`,
      activate: (userId: string) => `/v1/admin/users/${userId}/activate`,
      resendInvite: (userId: string) => `/v1/admin/users/${userId}/resend-invite`,
    },
    // Staff Schedules
    staffSchedules: {
      list: (propertyId: string) => `/v1/admin/staff-schedules/${propertyId}`,
      onDuty: (propertyId: string) => `/v1/admin/staff-schedules/${propertyId}/on-duty`,
      create: (propertyId: string) => `/v1/admin/staff-schedules/${propertyId}`,
      update: (propertyId: string, scheduleId: string) => `/v1/admin/staff-schedules/${propertyId}/${scheduleId}`,
      delete: (propertyId: string, scheduleId: string) => `/v1/admin/staff-schedules/${propertyId}/${scheduleId}`,
    },
    // Escalation
    escalation: {
      acknowledge: (propertyId: string, handoverId: string) => `/v1/admin/handovers/${propertyId}/${handoverId}/acknowledge`,
      resolve: (propertyId: string, handoverId: string) => `/v1/admin/handovers/${propertyId}/${handoverId}/resolve`,
      status: (propertyId: string, handoverId: string) => `/v1/admin/handovers/${propertyId}/${handoverId}/escalation`,
    },
    // Push Notifications
    push: {
      subscribe: (propertyId: string) => `/v1/admin/push/${propertyId}/subscribe`,
      unsubscribe: (propertyId: string) => `/v1/admin/push/${propertyId}/unsubscribe`,
      status: (propertyId: string) => `/v1/admin/push/${propertyId}/status`,
      vapidPublicKey: '/v1/admin/push/vapid-public-key',
    },
    // Test Chat
    testChat: {
      send: '/v1/admin/test-chat',
      stream: '/v1/admin/test-chat/stream',
    },
    // Dashboard Command Center
    dashboard: {
      commandCenter: (propertyId: string) => `/v1/admin/dashboard/${propertyId}/command-center`,
      briefing: (propertyId: string) => `/v1/admin/dashboard/${propertyId}/briefing`,
      insights: (propertyId: string) => `/v1/admin/dashboard/${propertyId}/insights`,
      orgSummary: (orgId: string) => `/v1/admin/dashboard/organizations/${orgId}/summary`,
      projectedValue: (propertyId: string) => `/v1/admin/dashboard/${propertyId}/projected-value`,
      proofMoments: (propertyId: string) => `/v1/admin/dashboard/${propertyId}/proof-moments`,
    },
    // System / Platform Health (super-admin)
    system: {
      dashboard: '/v1/admin/system/dashboard',
      metricsTrends: '/v1/admin/system/metrics/trends',
      errors: '/v1/admin/system/errors',
      errorDetail: (id: string) => `/v1/admin/system/errors/${id}`,
      errorStats: '/v1/admin/system/errors/stats',
      testDigest: '/v1/admin/system/test-digest',
    },
    // AI Ops (super-admin) — telemetry + persistent operation timelines
    aiOps: {
      dashboard: '/v1/admin/ai-ops/dashboard',
      operations: '/v1/admin/ai-ops/operations',
      anomalies: '/v1/admin/ai-ops/anomalies',
      operation: (id: string) => `/v1/admin/ai-ops/${id}`,
      operationEvents: (id: string) => `/v1/admin/ai-ops/${id}/events`,
      operationStream: (id: string) => `/v1/admin/ai-ops/${id}/stream`,
      operationFeedback: (id: string) => `/v1/admin/ai-ops/${id}/feedback`,
    },
    // AI Shadow Runs (super-admin) — A/B candidate validation. Configs are
    // process-local and reset on backend restart by design.
    aiShadow: {
      configs: '/v1/admin/ai-shadow/configs',
      configByTask: (task: string) => `/v1/admin/ai-shadow/configs/${task}`,
      results: '/v1/admin/ai-shadow/results',
    },
    // Auto-Config (post-scrape widget + automation configuration)
    autoConfig: {
      get: (propertyId: string) => `/v1/admin/auto-config/${propertyId}`,
      apply: (propertyId: string) => `/v1/admin/auto-config/${propertyId}/apply`,
      dismiss: (propertyId: string) => `/v1/admin/auto-config/${propertyId}/dismiss`,
    },
    // Settings
    settings: {
      get: (propertyId: string) => `/v1/admin/settings/${propertyId}`,
      update: (propertyId: string) => `/v1/admin/settings/${propertyId}`,
    },
  },
  websocket: {
    url: import.meta.env.VITE_WS_URL || 'ws://localhost:3003',
  },
};
